import * as React from 'react'
import { Check, ImagePlus, Loader2, Star, X } from 'lucide-react'
import type { DocumentDto } from '@lib/dto/document.dto'
import { useDocuments } from '@/hooks/useDocuments'
import { usePhotoUrl } from '@/hooks/usePhotos'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { errorMessage } from '@/helpers/logger'
import { cn } from '@/lib/utils'

/** A photo a proposal can show: a finished upload that has not been archived. */
export function isUsablePhoto(document: DocumentDto): boolean {
  return (
    document.kind === 'PHOTO' &&
    document.status === 'READY' &&
    document.archivedAt === null
  )
}

/**
 * True once the element has scrolled near the viewport.
 *
 * A thumbnail costs a request for a signed link, and a client surveyed tree by
 * tree can have a hundred photos. Asking only for the ones somebody can see
 * keeps opening the picker from firing a hundred requests at once.
 */
function useNearViewport(ref: React.RefObject<HTMLElement | null>): boolean {
  const [near, setNear] = React.useState(false)
  React.useEffect(() => {
    const element = ref.current
    if (element === null || near) return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [ref, near])
  return near
}

/** One photo, square-cropped, fetched once it is close to being seen. */
function Thumbnail({ photo }: { photo: DocumentDto }) {
  const ref = React.useRef<HTMLDivElement>(null)
  const near = useNearViewport(ref)
  const url = usePhotoUrl(photo.id, { enabled: near })
  const label = photo.title ?? photo.fileName

  function renderImage(): React.ReactNode {
    // A failed link is its own state. An empty tile used to be how a photo
    // that would not load passed for one still waiting to scroll into view.
    if (url.isError) {
      return (
        <div className="flex h-full items-center justify-center px-1 text-center text-[10px] leading-tight text-danger">
          Could not load
        </div>
      )
    }
    if (url.data === undefined) {
      return (
        <div className="flex h-full items-center justify-center">
          {url.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-text" />
          ) : null}
        </div>
      )
    }
    return (
      <img
        src={url.data}
        alt={label}
        title={label}
        className="h-full w-full object-cover"
        loading="lazy"
        decoding="async"
      />
    )
  }

  return (
    <div ref={ref} className="h-full w-full bg-subtle">
      {renderImage()}
    </div>
  )
}

interface ProposalPhotosProps {
  clientId: string
  /** Everything attached to the version, in order. Only photos are shown. */
  attached: DocumentDto[]
  editable: boolean
  disabled?: boolean
  /** The new order of attached document ids, photos first. */
  onChange: (documentIds: string[]) => void
}

/**
 * The photos a proposal shows beyond the survey's own.
 *
 * The survey already puts each tree's photos on its card, so this is only the
 * cover and a few extra pictures — the crew at work, a before-and-after. The
 * first photo is the cover, which is the one rule worth saying on screen,
 * since it is the first thing a client sees.
 *
 * Only photos are offered. A version can carry other kinds of file, but
 * nothing on the proposal reads them, and listing them here would suggest a
 * site map or a PDF reaches the client when it does not. Any already attached
 * are kept, after the photos, rather than dropped without a word.
 */
export function ProposalPhotos({
  clientId,
  attached,
  editable,
  disabled = false,
  onChange,
}: ProposalPhotosProps) {
  const [picking, setPicking] = React.useState(false)

  const photos = attached.filter(isUsablePhoto)
  const otherIds = attached
    .filter((document) => !isUsablePhoto(document))
    .map((document) => document.id)

  function commit(photoIds: string[]): void {
    onChange([...photoIds, ...otherIds])
  }

  function remove(id: string): void {
    commit(photos.filter((photo) => photo.id !== id).map((photo) => photo.id))
  }

  function makeCover(id: string): void {
    commit([id, ...photos.filter((photo) => photo.id !== id).map((p) => p.id)])
  }

  return (
    <div className="space-y-2">
      {photos.length === 0 ? (
        <p className="text-sm text-muted-text">
          No photos. The proposal opens on the site plan instead.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {photos.map((photo, index) => (
            <li
              key={photo.id}
              className="group relative h-20 w-20 overflow-hidden rounded-md border border-default"
            >
              <Thumbnail photo={photo} />
              {index === 0 ? (
                <span className="absolute bottom-1 left-1 rounded-sm bg-surface/90 px-1 text-xs font-medium text-primary-text">
                  Cover
                </span>
              ) : null}
              {editable ? (
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
                  {index === 0 ? null : (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        makeCover(photo.id)
                      }}
                      aria-label="Make this the cover"
                      title="Make this the cover"
                      className="flex h-6 w-6 items-center justify-center rounded-sm bg-surface/90 text-secondary-text hover:text-primary-text disabled:opacity-50"
                    >
                      <Star className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      remove(photo.id)
                    }}
                    aria-label="Remove from this proposal"
                    title="Remove from this proposal"
                    className="flex h-6 w-6 items-center justify-center rounded-sm bg-surface/90 text-secondary-text hover:text-primary-text disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {editable ? (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => {
            setPicking(true)
          }}
        >
          <ImagePlus className="h-3.5 w-3.5" />
          {photos.length === 0 ? 'Choose photos' : 'Change photos'}
        </Button>
      ) : null}

      {picking ? (
        <PhotoPickerDialog
          clientId={clientId}
          selected={photos.map((photo) => photo.id)}
          onCancel={() => {
            setPicking(false)
          }}
          onSave={(ids) => {
            setPicking(false)
            commit(ids)
          }}
        />
      ) : null}
    </div>
  )
}

interface PhotoPickerDialogProps {
  clientId: string
  selected: string[]
  onCancel: () => void
  onSave: (photoIds: string[]) => void
}

/**
 * Pick photos from this client's survey and the stock photos.
 *
 * Choices are held until Save, so trying a few covers is not a save per click
 * against a draft somebody else might be previewing.
 */
function PhotoPickerDialog({
  clientId,
  selected,
  onCancel,
  onSave,
}: PhotoPickerDialogProps) {
  const clientPhotos = useDocuments({ clientId, kind: 'PHOTO' })
  const stockPhotos = useDocuments({ library: true, kind: 'PHOTO' })
  const [chosen, setChosen] = React.useState<string[]>(selected)

  function toggle(id: string): void {
    setChosen((current) =>
      current.includes(id)
        ? current.filter((other) => other !== id)
        : [...current, id],
    )
  }

  const survey = clientPhotos.isSuccess
    ? clientPhotos.data.documents.filter(isUsablePhoto)
    : []
  const stock = stockPhotos.isSuccess
    ? stockPhotos.data.documents.filter(isUsablePhoto)
    : []
  const isPending = clientPhotos.isPending || stockPhotos.isPending
  const bothReady = clientPhotos.isSuccess && stockPhotos.isSuccess

  function libraryError(): string | null {
    if (clientPhotos.isError) {
      return errorMessage(clientPhotos.error, 'Photos could not be loaded.')
    }
    if (stockPhotos.isError) {
      return errorMessage(stockPhotos.error, 'Photos could not be loaded.')
    }
    return null
  }

  const libraryErrorMessage = libraryError()

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Choose photos</DialogTitle>
          <DialogDescription>
            The first one you pick is the cover. The rest appear under “From
            the field”. Each tree’s own photos are already on its card.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60svh] space-y-4 overflow-y-auto">
          {isPending ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
            </div>
          ) : null}
          {libraryErrorMessage === null ? null : (
            <p className="py-2 text-center text-sm text-danger">
              {libraryErrorMessage}
            </p>
          )}
          {bothReady && survey.length === 0 && stock.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-text">
              No photos yet. Photos taken during the survey show up here.
            </p>
          ) : null}
          <PhotoGrid
            title="From this client’s survey"
            photos={survey}
            chosen={chosen}
            onToggle={toggle}
          />
          <PhotoGrid
            title="Stock photos"
            photos={stock}
            chosen={chosen}
            onToggle={toggle}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            disabled={!bothReady}
            onClick={() => {
              onSave(chosen)
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface PhotoGridProps {
  title: string
  photos: DocumentDto[]
  chosen: string[]
  onToggle: (id: string) => void
}

function PhotoGrid({ title, photos, chosen, onToggle }: PhotoGridProps) {
  if (photos.length === 0) return null
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-medium text-secondary-text">{title}</h3>
      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {photos.map((photo) => {
          const position = chosen.indexOf(photo.id)
          const isChosen = position !== -1
          return (
            <li key={photo.id}>
              <button
                type="button"
                aria-pressed={isChosen}
                onClick={() => {
                  onToggle(photo.id)
                }}
                className={cn(
                  'relative block aspect-square w-full overflow-hidden rounded-md border-2 transition-colors',
                  isChosen
                    ? 'border-brand'
                    : 'border-transparent hover:border-strong',
                )}
              >
                <Thumbnail photo={photo} />
                {isChosen ? (
                  <span className="absolute top-1 right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1 text-xs font-medium text-primary-foreground">
                    {position === 0 ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      String(position + 1)
                    )}
                  </span>
                ) : null}
                {position === 0 ? (
                  <span className="absolute bottom-1 left-1 rounded-sm bg-surface/90 px-1 text-xs font-medium text-primary-text">
                    Cover
                  </span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
